import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import '../../../library/elements/d-menu.js';
import './d-infosec-view-header.js';
import './editors/d-data-items.js';
import '../../../library/editors/components/d-select-add.js';
import { sortSelectedCategories, getDataTypesBySector, getDataItemsBySector } from './defaults.js';
import './editors/d-edit-computers-network.js';
import type { InfosecDataType } from './infosecDataType';
import type { Sector } from '../../../store/types';
import type { InfosecDataItem } from './infosecDataItem';
import type { InfosecStorageUnit } from './infosecStorageUnit';
import type { InfosecNetwork } from './infosecNetwork';
import type { InfosecAsset } from './infosecAsset';
import type { SelectDropdownOption } from '../../../library/editors/elements/select-dropdown-option';

/**
 *
 */
@customElement('d-infosec-data')
export class DInfosecData extends LitElement {
  static readonly styles = [
    css`
      :host {
        display: block;
      }
      d-data-items {
        margin-top: -1px;
      }
    `,
  ];
  @property({ type: String })
  type = 'patientData';
  @property({ type: String })
  sector: Sector = 'OTHER';
  @property({ type: Array })
  dataItems: InfosecDataItem[] = [];
  @property({ type: Array })
  partners: SelectDropdownOption[] = [];
  @property({ type: Array })
  computers: InfosecStorageUnit[] = [];
  @property({ type: Array })
  remoteServers: InfosecStorageUnit[] = [];
  @property({ type: Array })
  cloudServices: InfosecStorageUnit[] = [];
  @property({ type: Array })
  locations: InfosecStorageUnit[] = [];
  @property({ type: Array })
  assets: InfosecAsset[] = [];
  @property({ type: Array })
  networks: InfosecNetwork[] = [];

  private get menuItems() {
    return getDataTypesBySector(this.sector).map((dataType) => {
      return {
        value: dataType.type,
        text: this._capitalize(dataType.name),
      };
    });
  }

  _capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  render() {
    const availableDataTypes = getDataTypesBySector(this.sector);

    // If current type is not available for this sector, switch to first available type
    const currentTypeAvailable = availableDataTypes.some((dt) => dt.type === this.type);
    if (!currentTypeAvailable && availableDataTypes.length > 0) {
      this.type = availableDataTypes[0].type;
    }

    const dataType: InfosecDataType = availableDataTypes.filter((type) => {
      return type.type === this.type;
    })[0];

    // If no data type is available (non-health sector and trying to show patient data), return empty
    if (!dataType) {
      return html``;
    }

    // Filter data items based on sector
    const filteredDataItems = getDataItemsBySector(this.dataItems, this.sector);

    return html`
      <d-menu
        small
        .items=${this.menuItems}
        .value=${this.type}
        @value-changed=${(e) => (this.type = e.detail.value)}
      ></d-menu>
      <d-infosec-view-header .title=${this._capitalize(dataType.name)} .info=${dataType.text}></d-infosec-view-header>
      <d-data-items
        .dataType=${dataType}
        .dataItems=${filteredDataItems}
        .cloudServices=${this.cloudServices}
        .remoteServers=${this.remoteServers}
        .computers=${this.computers}
        .assets=${this.assets}
        .locations=${this.locations}
        .partners=${this.partners}
      ></d-data-items>
    `;
  }

  protected firstUpdated() {
    const filteredDataItems = getDataItemsBySector(this.dataItems, this.sector);
    this.dataItems = sortSelectedCategories(filteredDataItems);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-infosec-data': DInfosecData;
  }
}
